/**
 * ContractRichEditor — 계약서 양식 편집기 (조합 컴포넌트)
 *
 *  편집 모델:
 *  - Doc          : contractPage+ (최상위는 페이지만)
 *  - ContractPage : A4 794×1123px 캔버스
 *      ├─ paragraph (직접 작성)
 *      ├─ positionedText (PDF 임포트가 emit — 절대좌표 한 줄)
 *      └─ contractField (레거시 호환 전용 — 신규 필드는 Step 2 필드 설정에서 배치)
 *
 *  주요 기능:
 *  - 자동 페이지네이션 (워드 식): 본문이 페이지 끝 도달 시 다음 페이지로 흘려보냄
 *  - 페이지 안 안전 영역 클램프 (필드가 페이지 밖으로 못 나감)
 *  - 변수 인서트
 *  - 슬래시(/) / 대괄호([) 트리거 메뉴 — 변수 빠른 삽입
 *
 *  좌표·정규화·페이지네이션·필드 NodeView 등 무거운 로직은 editor/ 폴더로 분리됨.
 */
import {
  EditorContent,
  Extension,
  ReactNodeViewRenderer,
  useEditor,
  type Editor,
} from "@tiptap/react";
import { TextSelection, NodeSelection } from "@tiptap/pm/state";
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Image } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { PanZoomCanvas, type PanZoomHandle } from "./PanZoomCanvas";
import { AUTO_VARIABLES } from "./contractTemplate";
import { useSealList } from "~/services/business/seal";
import { fieldColor, fieldIcon, friendlyFieldLabel } from "./editor/nodes";

import { A4 } from "./editor/coords";
import {
  ContractDoc,
  ContractPageNode,
  PositionedTextNode,
  PositionedImageNode,
  buildContractFieldNode,
  buildContractVariableNode,
} from "./editor/nodes";
import {
  LEGACY_KEY_MAP,
  emptyDocWithPage,
  jsonToPlainMarker,
  normalizeDoc,
  plainToJson as _plainToJson,
} from "./editor/normalize";
import {
  SignFieldNodeView,
  createSignStore,
  type SignStore,
} from "./editor/SignFieldNodeView";
import { FieldPlacementNodeView } from "./editor/FieldPlacementNodeView";
import { buildPaginationPlugin } from "./editor/pagination";
import { buildTableNormalizePlugin } from "./editor/tableNormalize";
import { ContractEditorToolbar } from "./editor/Toolbar";
import { ContractEditorStatusBar } from "./editor/StatusBar";

/* ─────────────────────────────────────────────────────────────────
 *  Public exports — 기존 코드와 호환
 *  legacy plainToJson(text) 시그니처 보존 — vars 는 자동으로 ALL_VARS 주입
 * ───────────────────────────────────────────────────────────────── */
export const plainToJson = (text: string): any => _plainToJson(text, ALL_VARS);
export { jsonToPlainMarker };

/**
 * 계약서 렌더 폰트 결정론화(cross-OS 정합).
 * 편집기(브라우저)는 Windows=맑은고딕, 서버 Playwright(Linux)는 Noto 폴백이라
 * 필드 좌표(편집기 기준 배치)와 baked(서버 렌더)가 OS별로 어긋났다.
 * 해결: docJson 에 인라인으로 박힌 폰트 이름("Malgun Gothic"/"맑은 고딕"/…) 자체를
 * 번들 Noto Sans KR 로 @font-face aliasing → 편집기·서버가 동일 파일을 사용.
 * (인라인 폰트 우선 설계는 그대로. 이름만 재정의하므로 AI 임포트 자간/줄간격은 유지)
 * 서버(PlaywrightContractTemplateRenderService.stylesheet())도 동일 폰트를 data-URI 로 심는다.
 */
const _contractFontFace = (family: string) => `
  @font-face { font-family: ${family}; font-weight: 400; font-style: normal; font-display: block;
    src: url("/woff2/noto-sans-kr-korean-400-normal.woff2") format("woff2"); }
  @font-face { font-family: ${family}; font-weight: 400; font-style: normal; font-display: block;
    src: url("/woff2/noto-sans-kr-latin-400-normal.woff2") format("woff2");
    unicode-range: U+0000-00FF, U+2000-206F, U+2070-209F, U+20A0-20CF; }
  @font-face { font-family: ${family}; font-weight: 700; font-style: normal; font-display: block;
    src: url("/woff2/noto-sans-kr-korean-700-normal.woff2") format("woff2"); }
  @font-face { font-family: ${family}; font-weight: 700; font-style: normal; font-display: block;
    src: url("/woff2/noto-sans-kr-latin-700-normal.woff2") format("woff2");
    unicode-range: U+0000-00FF, U+2000-206F, U+2070-209F, U+20A0-20CF; }`;
export const CONTRACT_FONT_FACE_CSS = [
  '"Malgun Gothic"',
  '"맑은 고딕"',
  '"Apple SD Gothic Neo"',
  '"Noto Sans CJK KR"',
]
  .map(_contractFontFace)
  .join("\n");

/**
 * contract-page-canvas 공통 스타일.
 * 본문 편집기(Step 1) 와 필드 배치 배경(Step 2) 이 동일하게 렌더되도록
 * 이 스타일 문자열을 양쪽에서 <style> 로 주입한다.
 */
export const CONTRACT_PAGE_CSS = `
${CONTRACT_FONT_FACE_CSS}
  /* :where() 로 specificity 0 — inline style / paragraph attrs 가 항상 우선.
     AI 임포트 문서가 원본 폰트·자간·줄간격을 지정하면 그대로 반영됨. */
  :where(.contract-page-canvas p) {
    margin-top: 0; margin-bottom: 0;
    line-height: 1.5; letter-spacing: -0.04em;
    font-family: "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", "Noto Sans CJK KR", sans-serif;
    font-size: 10pt; color: #111;
  }
  :where(.contract-page-canvas p + p) { margin-top: 0; }
  .contract-page-canvas > p,
  .contract-page-canvas > h1,
  .contract-page-canvas > h2,
  .contract-page-canvas > h3 {
    margin-left: 75px; margin-right: 75px;
    width: 644px; max-width: 644px; box-sizing: border-box;
    overflow-wrap: anywhere; word-break: keep-all; white-space: pre-wrap;
    tab-size: 4; -moz-tab-size: 4;
  }
  .contract-page-canvas > p[style*="text-align: center"],
  .contract-page-canvas > p[style*="text-align:center"]   { text-align: center !important; }
  .contract-page-canvas > p[style*="text-align: right"],
  .contract-page-canvas > p[style*="text-align:right"]    { text-align: right !important; }
  .contract-page-canvas > p[style*="text-align: justify"],
  .contract-page-canvas > p[style*="text-align:justify"]  { text-align: justify !important; }
  .contract-page-canvas > p[data-text-align="center"]  { text-align: center !important; }
  .contract-page-canvas > p[data-text-align="right"]   { text-align: right !important; }
  .contract-page-canvas > p[data-text-align="justify"] { text-align: justify !important; }
  .contract-page-canvas::before {
    content: ""; display: block; height: 75px; width: 100%;
  }
  .contract-page-canvas > p:first-child,
  .contract-page-canvas > h1:first-child,
  .contract-page-canvas > h2:first-child,
  .contract-page-canvas > h3:first-child,
  .contract-page-canvas > div.tableWrapper:first-child,
  .contract-page-canvas > table:first-child { padding-top: 0; }
  .contract-page-canvas table.contract-table,
  .contract-page-canvas table {
    border-collapse: collapse; margin-top: 6pt; margin-bottom: 6pt;
    margin-left: 75px !important; margin-right: 75px !important;
    table-layout: fixed;
    width: 644px !important; min-width: 644px !important; max-width: 644px !important;
    box-sizing: border-box;
    font-family: "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", "Noto Sans CJK KR", sans-serif;
    font-size: 11pt;
  }
  .contract-page-canvas table colgroup { width: 644px !important; max-width: 644px !important; }
  .contract-page-canvas table col { max-width: 644px; }
  .contract-page-canvas .tableWrapper {
    margin-left: 75px !important; margin-right: 75px !important;
    max-width: 644px !important; width: 644px !important;
    overflow-x: hidden; box-sizing: border-box;
  }
  .contract-page-canvas .tableWrapper > table { margin-left: 0 !important; margin-right: 0 !important; }
  .contract-page-canvas table th,
  .contract-page-canvas table td {
    border: 0.6pt solid #969696; padding: 2pt 4pt; vertical-align: middle;
    line-height: 1.3; word-break: break-all; overflow-wrap: anywhere; overflow: hidden;
  }
  .contract-page-canvas table p { line-height: 1.3; letter-spacing: -0.04em; }
  .contract-page-canvas > * { max-width: 794px !important; box-sizing: border-box; }
  .contract-page-canvas table th { background: #F5F5F5; font-weight: 700; }
  .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[style*="text-align: center"]),
  .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[style*="text-align:center"]),
  .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[data-text-align="center"]) {
    background: #F5F5F5; font-weight: 700;
  }
  .contract-page-canvas table tr > td:only-child { padding-left: 0; padding-right: 0; }
  .contract-page-canvas table p,
  .contract-page-canvas table th p,
  .contract-page-canvas table td p {
    margin-left: 0 !important; margin-right: 0 !important;
    padding-top: 0 !important; max-width: none !important;
  }
  .contract-page-canvas table td p + p,
  .contract-page-canvas table th p + p { margin-top: 0 !important; }
`;

export interface RichEditorHandle {
  getJSON: () => any;
  getText: () => string;
  /** 현재 PanZoomCanvas 의 zoom (0.25 ~ 3). 오버레이 드래그 좌표 보정용. */
  getZoom: () => number;
  /** 단일레이어 필드 삽입 — 현재 커서 위치에 inline contractField 노드를 넣는다. (필드 설정 모드 툴바용) */
  insertField: (type: string) => void;
  /** 서명 모드: 현재 입력된 필드 값 수집 { fields, checkboxes, signature }. */
  getSignValues: () => {
    fields: Record<string, string>;
    checkboxes: Record<string, boolean>;
    signature: string | null;
  };
  /** 서명 모드: 필수 미입력 시 에러 메시지, 없으면 null. */
  validateSign: () => string | null;
  /** 서명 모드: 진행률(필수 기준 done/total + 선택 기준 optionalDone/optionalTotal). */
  getSignProgress: () => {
    done: number;
    total: number;
    optionalDone: number;
    optionalTotal: number;
  };
  /** 서명 모드: 아직 안 채운 첫 필드로 스크롤(+서명이면 패드 오픈). */
  scrollToNextIncomplete: () => void;
}

/** 서명 모드에서 보호자가 채워야 하는 필드 타입(도장·seal 은 사업자 값이라 제외). */
const GUARDIAN_FILLABLE = new Set(["signature", "checkbox", "text", "longText", "date", "address"]);

/** doc 에서 보호자 입력 대상 필드 목록 수집 (진행률 total 산출용). */
const collectSignFields = (
  doc: any,
  out: { type: string; key: string; required: boolean }[] = [],
): { type: string; key: string; required: boolean }[] => {
  if (!doc || typeof doc !== "object") return out;
  if (doc.type === "contractField") {
    const a = doc.attrs || {};
    const type = a.fieldType || "text";
    if (GUARDIAN_FILLABLE.has(type)) {
      out.push({ type, key: a.fieldKey || "", required: a.required !== false });
    }
  }
  if (Array.isArray(doc.content)) doc.content.forEach((c: any) => collectSignFields(c, out));
  return out;
};

/* 필드 기본 key/label — ContractFieldOverlay 의 defaultKey/defaultLabel 과 동일 규칙 */
const FIELD_LABELS: Record<string, string> = {
  signature: "보호자 서명",
  seal: "사업장 도장",
  checkbox: "체크박스",
  address: "주소",
  text: "텍스트",
  longText: "긴 텍스트",
  date: "날짜",
};
const fieldDefaultLabel = (type: string) => FIELD_LABELS[type] ?? "필드";
const fieldDefaultKey = (type: string) => {
  const s = Date.now();
  switch (type) {
    case "signature":
      return "guardianSign";
    case "address":
      return "guardianAddress";
    case "seal":
      return "companySeal";
    case "date":
      return `fillDate_${s}`;
    case "checkbox":
      return `checkbox_${s}`;
    case "longText":
      return `longText_${s}`;
    default:
      return `text_${s}`;
  }
};

interface Props {
  initialJson?: any;
  initialText?: string;
  onChange?: (json: any, plainMarker: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  /**
   * PanZoomCanvas 안에 EditorContent 와 함께 렌더될 오버레이 노드.
   * 편집기 자체 좌표계(0..A4.WIDTH_PX × 페이지스택 전체 높이) 안에서
   * absolute 좌표를 사용해 필드 박스 등을 얹을 때 사용.
   * PanZoomCanvas 의 scale 이 자동 적용되므로 zoom 과 함께 함께 확대/축소됨.
   */
  overlay?: React.ReactNode;
  /**
   * 필드 설정 모드 (Step 2). true 면:
   *  - 텍스트 입력 차단 (본문·변수는 못 건드림) → "필드일 때는 필드만"
   *  - 필드(inline contractField) 클릭 시 필수/선택·도장선택·삭제 팝오버 노출
   * false(본문 모드) 면 텍스트·변수만 편집하고 필드 팝오버는 안 뜬다.
   */
  fieldMode?: boolean;
  /**
   * 서명 모드 (보호자 서명 화면). true 면:
   *  - inline contractField 를 인터랙티브 SignFieldNodeView 로 렌더(서명패드·체크박스·입력)
   *  - contractVariable 을 signValues 값으로 치환 렌더
   *  - 편집기 엔진(pagination·normalize·CSS) 을 그대로 재사용 → 서명화면=편집기 자동 일치
   * readOnly 와 함께 쓴다(본문 편집 불가, 필드만 입력).
   */
  signMode?: boolean;
  /** 서명 모드: 변수 값 맵(varKey→값). 채워진 변수는 값으로 렌더. */
  signValues?: Record<string, string | null | undefined> | null;
  /** 서명 모드: 도장 이미지 URL(사업자 도장 필드 표시용). */
  sealImageUrl?: string | null;
  /** 서명 모드: 필드 입력 진행률 변경 콜백(필수 done/total + 선택 optionalDone/optionalTotal). */
  onSignProgressChange?: (p: {
    done: number;
    total: number;
    optionalDone: number;
    optionalTotal: number;
  }) => void;
  /**
   * 서명 모드에서 필드까지 읽기전용(입력 불가)으로 잠글지.
   * 본문 잠금(readOnly)과 별개 — 대기중 계약은 false(필드 입력 가능),
   * 이미 완료/거절된 계약 열람은 true(필드도 잠금).
   */
  signReadOnly?: boolean;
  /**
   * 이미 서명·제출 완료된 계약 재열람용 — 보호자가 채운 값으로 필드를 시드한다.
   * signatureUrl = 제출한 서명 이미지, values = fieldKey→입력값(체크박스는 "Y"/"N").
   */
  signSubmitted?: {
    signatureUrl?: string | null;
    values?: Record<string, string | null | undefined> | null;
  } | null;
}

/* ─────────────────────────────────────────────────────────────────
 *  트리거 메뉴 (슬래시 / 대괄호) — 빠른 삽입
 * ───────────────────────────────────────────────────────────────── */
const ALL_VARS = [...AUTO_VARIABLES];

type TriggerItem = {
  kind: "variable";
  key: string;
  label: string;
  group: "auto";
};

const buildTriggerItems = (
  _type: "slash" | "bracket",
  query: string,
): TriggerItem[] => {
  const q = query.toLowerCase();
  const matches = (s: string) => s.toLowerCase().includes(q);
  const variables: TriggerItem[] = AUTO_VARIABLES.map((v) => ({
    kind: "variable" as const,
    key: v.key,
    label: v.label,
    group: "auto" as const,
  })).filter((v) => !q || matches(v.label) || matches(v.key));

  return variables.slice(0, 12);
};

/* ─────────────────────────────────────────────────────────────────
 *  키보드 단축키 핸들러
 * ───────────────────────────────────────────────────────────────── */

/** Ctrl/Cmd + Enter — 현재 커서가 있는 contractPage 직후에 새 contractPage 삽입.
 *  사용자 cursor 는 새 페이지의 첫 paragraph 시작점으로 이동. */
const insertManualPageBreak = (view: import("@tiptap/pm/view").EditorView) => {
  const { state } = view;
  const pageType = state.schema.nodes.contractPage;
  const paraType = state.schema.nodes.paragraph;
  if (!pageType || !paraType) return;

  // 현재 커서가 속한 contractPage 찾기
  const selFrom = state.selection.from;
  let pagePos = -1;
  let pageNode: any = null;
  state.doc.forEach((n, off) => {
    if (
      n.type.name === "contractPage" &&
      selFrom > off &&
      selFrom < off + n.nodeSize
    ) {
      pagePos = off;
      pageNode = n;
    }
  });
  if (pagePos < 0 || !pageNode) return;

  const pageEnd = pagePos + pageNode.nodeSize;
  // 페이지 number 는 pagination 플러그인이 이후 cycle 에서 재정렬
  const newPage = pageType.create(
    { pageNumber: 0, width: A4.WIDTH_PX, height: A4.HEIGHT_PX },
    paraType.create(),
  );
  const tr = state.tr.insert(pageEnd, newPage);
  // 새 페이지 첫 paragraph 안 (= pageEnd + 1 + 1 = pageEnd + 2) 으로 cursor 이동
  try {
    const newSelPos = pageEnd + 2;
    tr.setSelection(TextSelection.near(tr.doc.resolve(newSelPos)));
  } catch {
    /* ignore */
  }
  tr.scrollIntoView();
  view.dispatch(tr);
};

/** Tab / Shift+Tab — 현재 paragraph 의 시작에 4 spaces 삽입/제거 (간단한 들여쓰기).
 *  스키마 변경 없이 텍스트 레벨에서만 처리. */
const adjustParagraphIndent = (
  view: import("@tiptap/pm/view").EditorView,
  direction: 1 | -1,
) => {
  const { state } = view;
  const $from = state.selection.$from;
  const paraStart = $from.start();
  const tr = state.tr;
  if (direction > 0) {
    tr.insertText("    ", paraStart);
  } else {
    // 시작에 4-space prefix 가 있으면 제거
    const text = state.doc.textBetween(paraStart, paraStart + 4, "\n", "\n");
    if (text === "    ") tr.delete(paraStart, paraStart + 4);
    else return;
  }
  view.dispatch(tr);
};

/* ─────────────────────────────────────────────────────────────────
 *  메인 컴포넌트
 * ───────────────────────────────────────────────────────────────── */
export const ContractRichEditor = forwardRef<RichEditorHandle, Props>(
  (
    {
      initialJson,
      initialText,
      onChange,
      placeholder = "본문을 입력하세요. / 또는 [ 를 누르면 변수 삽입 메뉴가 열립니다.",
      readOnly = false,
      overlay,
      fieldMode = false,
      signMode = false,
      signValues,
      sealImageUrl,
      onSignProgressChange,
      signReadOnly = false,
      signSubmitted,
    },
    ref,
  ) => {
    /* ── 상태 ──────────────────────────────────────────── */
    const [trigger, setTrigger] = useState<{
      type: "slash" | "bracket";
      from: number;
      query: string;
      rect: { left: number; top: number; bottom: number };
    } | null>(null);
    const [highlightIdx, setHighlightIdx] = useState(0);

    const panZoomRef = useRef<PanZoomHandle | null>(null);
    const didSyncInitialDocRef = useRef(false);
    const initialDocRef = useRef<any | null>(null);
    const readOnlyRef = useRef(readOnly);
    const onChangeRef = useRef(onChange);
    const [zoomPct, setZoomPct] = useState(100);

    /* 필드 설정 모드 — 텍스트 입력 차단 + 필드 클릭 팝오버 */
    const fieldModeRef = useRef(fieldMode);
    const [fieldSel, setFieldSel] = useState<{
      from: number;
      attrs: Record<string, any>;
      rect: { left: number; top: number; bottom: number };
    } | null>(null);
    // 필드 이동/리사이즈 드래그 중에는 팝오버를 잠시 숨긴다(거슬림 방지).
    const [fieldDragging, setFieldDragging] = useState(false);
    // 커서 미배치 상태로 필드 추가 시 안내 토스트.
    const [caretNotice, setCaretNotice] = useState(false);
    const caretNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showCaretNotice = useCallback(() => {
      setCaretNotice(true);
      if (caretNoticeTimer.current) clearTimeout(caretNoticeTimer.current);
      caretNoticeTimer.current = setTimeout(() => setCaretNotice(false), 2600);
    }, []);
    useEffect(() => {
      const onDrag = (e: Event) => {
        setFieldDragging(!!(e as CustomEvent).detail);
      };
      // 변수 삽입 등 다른 곳에서도 "커서 먼저 놓으세요" 안내를 띄울 수 있게 이벤트 수신
      const onNeedCaret = () => showCaretNotice();
      window.addEventListener("contract-field-drag", onDrag);
      window.addEventListener("contract-need-caret", onNeedCaret);
      return () => {
        window.removeEventListener("contract-field-drag", onDrag);
        window.removeEventListener("contract-need-caret", onNeedCaret);
      };
    }, [showCaretNotice]);
    // 도장 목록은 필드설정 팝오버의 "도장 선택" 드롭다운에서만 쓴다(사업자 전용).
    // 보호자 서명화면(signMode)도 이 편집기를 재사용하므로, 여기서 무조건 호출하면
    // 보호자 토큰으로 사업자 API(/api/business/seal) 를 때려 403 이 난다. → fieldMode 에서만 조회.
    const { data: sealList = [] } = useSealList({
      enabled: fieldMode && !signMode,
    });
    const activeSeals = sealList.filter((s) => s.activeYn === "Y");

    useEffect(() => {
      fieldModeRef.current = fieldMode;
      if (!fieldMode) setFieldSel(null);
    }, [fieldMode]);

    useEffect(() => {
      readOnlyRef.current = readOnly;
    }, [readOnly]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    if (initialDocRef.current == null) {
      initialDocRef.current = initialJson
        ? normalizeDoc(initialJson, ALL_VARS)
        : initialText
          ? normalizeDoc(_plainToJson(initialText, ALL_VARS), ALL_VARS)
          : emptyDocWithPage();
    }

    /* ── 서명 모드 스토어 ─────────────────────────────── */
    const onSignProgressRef = useRef(onSignProgressChange);
    useEffect(() => {
      onSignProgressRef.current = onSignProgressChange;
    }, [onSignProgressChange]);
    const signFieldsRef = useRef<
      { type: string; key: string; required: boolean }[]
    >([]);
    if (signMode && signFieldsRef.current.length === 0) {
      signFieldsRef.current = collectSignFields(initialDocRef.current);
    }
    const signStoreRef = useRef<SignStore | null>(null);
    const pushSignProgress = (store?: SignStore | null) => {
      const s = store ?? signStoreRef.current;
      if (!s) return;
      const fields = signFieldsRef.current;
      const isFilled = (f: { type: string; key: string }) => {
        if (f.type === "signature") return !!s.signature;
        if (f.type === "checkbox") return !!s.checkboxes[f.key];
        return !!s.values[f.key];
      };
      // 진행률/게이트는 "필수" 필드 기준. 선택 필드는 채우면 카운트만 되고 제출을 막지 않는다.
      let done = 0;
      let total = 0;
      let optionalDone = 0;
      let optionalTotal = 0;
      fields.forEach((f) => {
        if (f.required) {
          total++;
          if (isFilled(f)) done++;
        } else {
          optionalTotal++;
          if (isFilled(f)) optionalDone++;
        }
      });
      onSignProgressRef.current?.({
        done,
        total,
        optionalDone,
        optionalTotal,
      });
    };
    if (signMode && !signStoreRef.current) {
      const store = createSignStore(
        sealImageUrl ?? null,
        signReadOnly,
        (s) => pushSignProgress(s),
      );
      // 이미 서명·제출된 계약을 다시 열 때: 보호자가 채운 값(서명 이미지·필드값)으로 시드해
      // 필드가 "채워진 그대로" 읽기전용 표시되게 한다.
      if (signSubmitted) {
        const vals = signSubmitted.values ?? {};
        signFieldsRef.current.forEach((f) => {
          if (f.type === "signature") {
            if (signSubmitted.signatureUrl) store.signature = signSubmitted.signatureUrl;
          } else if (f.type === "checkbox") {
            const v = vals[f.key];
            if (v != null) store.checkboxes[f.key] = v === "Y" || v === "true";
          } else {
            const v = vals[f.key];
            if (v != null && v !== "") store.values[f.key] = String(v);
          }
        });
      }
      signStoreRef.current = store;
    }

    /* ── 에디터 인스턴스 ──────────────────────────────── */
    const editor = useEditor({
      editable: !readOnly,
      extensions: [
        StarterKit.configure({
          document: false, // ContractDoc 으로 대체
          heading: false, // 사용 안 함
          link: { openOnClick: false },
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          horizontalRule: false,
          codeBlock: false,
          code: false,
        }),
        ContractDoc,
        TextStyleKit,
        // TextAlign — paragraph 만. defaultAlignment 는 null 유지 (TipTap 기본값) —
        // 그래야 textAlign 미설정 단락이 inline style="text-align: left" 로 강제 출력되지 않음.
        // ("left" 로 설정해두면 TipTap renderHTML 이 모든 단락에 inline style 을 박아버림 → CSS 우선순위 문제 야기)
        TextAlign.configure({
          types: ["paragraph"],
          alignments: ["left", "center", "right", "justify"],
        }),
        // 단락·인라인 추가 속성 — 임포트(HWPX/HWP/DOCX)가 attrs 에 박은 부속 스타일 보존
        //
        //   paragraph 타입:
        //     • lineHeight       : "1.6" / "12pt" 등 — CSS line-height 그대로 통과
        //     • textIndent       : "20.0pt" 등 — 첫 줄 들여쓰기
        //     • marginLeft       : "10.0pt" 등 — 단락 왼쪽 여백
        //     • marginRight      : "10.0pt" 등 — 단락 오른쪽 여백
        //     • pageBreakBefore  : true → CSS page-break-before: always (PDF/인쇄)
        //
        //   textStyle mark:
        //     • letterSpacing    : "-0.10em" 등 — 자간 (HWPX <hh:spacing hangul=...>)
        //
        //   TipTap 표준 schema 에 없는 attr 이므로 globalAttributes 로 명시 등록해야
        //   setContent / serialize 가 보존됨.
        Extension.create({
          name: "contractTypography",
          addGlobalAttributes() {
            /** style 문자열들을 합치는 util — 여러 attr 의 renderHTML 이 동일 style 키를 채울 때 충돌 방지 */
            const styleAttr = (
              decls: Array<string | null | undefined | false>,
            ) => {
              const s = decls.filter(Boolean).join(";");
              return s ? { style: s } : {};
            };
            return [
              {
                types: ["paragraph"],
                attributes: {
                  lineHeight: {
                    default: null,
                    parseHTML: (el) => el.style.lineHeight || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.lineHeight && `line-height:${a.lineHeight}`,
                      ]),
                  },
                  textIndent: {
                    default: null,
                    parseHTML: (el) => el.style.textIndent || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.textIndent && `text-indent:${a.textIndent}`,
                      ]),
                  },
                  marginLeft: {
                    default: null,
                    parseHTML: (el) => el.style.marginLeft || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.marginLeft && `margin-left:${a.marginLeft}`,
                      ]),
                  },
                  marginRight: {
                    default: null,
                    parseHTML: (el) => el.style.marginRight || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.marginRight && `margin-right:${a.marginRight}`,
                      ]),
                  },
                  marginTop: {
                    default: null,
                    parseHTML: (el) => el.style.marginTop || null,
                    renderHTML: (a) =>
                      styleAttr([a.marginTop && `margin-top:${a.marginTop}`]),
                  },
                  marginBottom: {
                    default: null,
                    parseHTML: (el) => el.style.marginBottom || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.marginBottom && `margin-bottom:${a.marginBottom}`,
                      ]),
                  },
                  // 원본 계약서의 섹션 구분 가로선(라인선) 재현용 — 문단 위/아래 테두리.
                  //  AI 임포트가 "빈 문단 + borderTop" 으로 구분선을 emit → 모든 렌더 경로 통과.
                  borderTop: {
                    default: null,
                    parseHTML: (el) => el.style.borderTop || null,
                    renderHTML: (a) =>
                      styleAttr([a.borderTop && `border-top:${a.borderTop}`]),
                  },
                  borderBottom: {
                    default: null,
                    parseHTML: (el) => el.style.borderBottom || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.borderBottom && `border-bottom:${a.borderBottom}`,
                      ]),
                  },
                  fontSize: {
                    default: null,
                    parseHTML: (el) => el.style.fontSize || null,
                    renderHTML: (a) =>
                      styleAttr([a.fontSize && `font-size:${a.fontSize}`]),
                  },
                  fontFamily: {
                    default: null,
                    parseHTML: (el) => el.style.fontFamily || null,
                    renderHTML: (a) =>
                      styleAttr([
                        a.fontFamily && `font-family:${a.fontFamily}`,
                      ]),
                  },
                  pageBreakBefore: {
                    default: null,
                    parseHTML: (el) =>
                      el.getAttribute("data-page-break") === "true" || null,
                    renderHTML: (a) =>
                      a.pageBreakBefore
                        ? {
                            "data-page-break": "true",
                            style: "page-break-before:always",
                          }
                        : {},
                  },
                },
              },
              {
                types: ["textStyle"],
                attributes: {
                  letterSpacing: {
                    default: null,
                    parseHTML: (el: HTMLElement) =>
                      el.style.letterSpacing || null,
                    renderHTML: (a: Record<string, any>) =>
                      a.letterSpacing
                        ? { style: `letter-spacing:${a.letterSpacing}` }
                        : {},
                  },
                },
              },
            ];
          },
        }),
        // HWP 항목 hanging indent — ①②③④⑤... 같은 항목 단락의 2행부터 들여쓰기.
        //   contractExtract 에서 paragraph.attrs.hwpHangingIndent = true 로 마킹된 단락에 적용.
        //   TipTap 의 Extension.create globalAttributes 로 paragraph 에 attr 추가.
        Extension.create({
          name: "hwpHangingIndent",
          addGlobalAttributes() {
            return [
              {
                types: ["paragraph"],
                attributes: {
                  hwpHangingIndent: {
                    default: null,
                    parseHTML: (el) =>
                      el.getAttribute("data-hwp-hanging") === "true" || null,
                    renderHTML: (attrs) => {
                      if (!attrs.hwpHangingIndent) return {};
                      return {
                        "data-hwp-hanging": "true",
                        style: "padding-left: 1.6em; text-indent: -1.6em;",
                      };
                    },
                  },
                },
              },
            ];
          },
        }),
        // 표 — 임포트된 계약서의 표 구조 보존용 (툴바에서 직접 삽입 UI 는 제공 안 함).
        // resizable: true 로 셀 width 조절 가능.
        Table.configure({
          resizable: true,
          HTMLAttributes: { class: "contract-table" },
        }),
        TableRow,
        TableHeader,
        TableCell,
        Image,
        Placeholder.configure({ placeholder }),
        buildContractVariableNode(
          ALL_VARS,
          LEGACY_KEY_MAP,
          signMode ? signValues : undefined,
        ),
        // 단일레이어: 필드는 inline atom.
        //  서명 모드 → 인터랙티브 SignFieldNodeView / 그 외 → renderHTML(정적 인라인 박스).
        buildContractFieldNode(
          signMode
            ? ReactNodeViewRenderer(SignFieldNodeView)
            : // 필드 설정(배치) 편집 화면에서만 모서리 드래그 리사이즈 NodeView.
              //  본문작성·검토·배경 렌더(readOnly)는 정적 renderHTML 유지(베이크 실측 안전).
              !readOnly && fieldMode
              ? ReactNodeViewRenderer(FieldPlacementNodeView)
              : undefined,
        ),
        // 서명 모드: editor.storage.sign 에 스토어 노출(NodeView 가 값 read/write).
        ...(signMode && signStoreRef.current
          ? [
              Extension.create({
                name: "sign",
                addStorage: () => signStoreRef.current as SignStore,
              }),
            ]
          : []),
        ContractPageNode,
        PositionedTextNode,
        PositionedImageNode,
      ],
      content: initialDocRef.current,
      editorProps: {
        attributes: {
          class:
            "prose-sm max-w-none focus:outline-none min-h-[400px] px-4 py-4 text-[14px] leading-relaxed",
          // 필드 모드는 글자 입력을 막으므로(handleTextInput swallow) 깜박이는
          // 텍스트 커서가 오해를 준다 → caret 숨김. 본문 작성 모드는 유지.
          ...(fieldMode ? { style: "caret-color: transparent" } : {}),
        },
        // 필드 모드: 텍스트 입력을 삼켜 본문/변수가 안 바뀌게 함 (필드 선택/삭제만 허용)
        handleTextInput: () => (fieldModeRef.current ? true : false),
        // 인라인 필드 클릭 → NodeSelection 으로 선택(기본은 caret 이라 팝오버가 안 뜸).
        //  선택되면 onSelectionUpdate → detectFieldSelection 이 설정 팝오버를 띄운다.
        handleClickOn: (view, _pos, node, nodePos) => {
          if (!fieldModeRef.current) return false;
          if (node?.type?.name === "contractField") {
            try {
              const tr = view.state.tr.setSelection(
                NodeSelection.create(view.state.doc, nodePos),
              );
              view.dispatch(tr);
              return true;
            } catch {
              return false;
            }
          }
          return false;
        },
        handleKeyDown: (view, e) => {
          if (fieldModeRef.current) {
            // 단축키(줌 등)는 통과. 필드 삭제·네비게이션 키 허용, 문자/개행은 차단.
            if (!(e.ctrlKey || e.metaKey || e.altKey)) {
              const nav = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Home",
                "End",
                "Escape",
                "Tab",
              ];
              if (e.key.length === 1 || e.key === "Enter") {
                e.preventDefault();
                return true;
              }
              if (!nav.includes(e.key)) return false;
            }
          }
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            insertManualPageBreak(view);
            return true;
          }
          if (e.key === "Tab") {
            const node = view.state.selection.$from.node();
            if (node?.type?.name === "paragraph") {
              e.preventDefault();
              adjustParagraphIndent(view, e.shiftKey ? -1 : 1);
              return true;
            }
          }
          return false;
        },
        /** ─── paste 정책 (최소 개입) ─────────────────────────────────────
         *  - 우리가 명시적으로 처리하는 것만 손대고, 나머지는 PM 의 schema 분석 그대로 위임.
         *  - 내부 ↔ 내부 paste: PM 클립보드 마커(data-pm-slice) 로 자동 인식돼 원본 100% 보존.
         *  - 외부 → 내부 paste: 위험·불필요 요소만 제거 → PM 의 DOMParser 가 우리 schema 에 맞춰 변환.
         *    (heading/list/table 등 비활성 노드는 PM 이 자동으로 paragraph 로 강등)
         *  - bold/italic/underline/strike/textStyle 마크는 PM 이 schema 로 보존. */
        transformPastedHTML: (html) => {
          if (!html) return html;
          // PM 자체 데이터 — 손대지 않음
          if (/data-pm-slice/.test(html)) return html;
          try {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            tmp
              .querySelectorAll(
                "script, style, meta, link, iframe, object, embed, noscript, template",
              )
              .forEach((n) => n.remove());
            return tmp.innerHTML;
          } catch {
            return html;
          }
        },
        transformPastedText: (text) => text.replace(/\r\n?/g, "\n"),
      },
      onUpdate: ({ editor, transaction }: any) => {
        if (readOnlyRef.current) return;
        if (transaction?.getMeta?.("addToHistory") === false) {
          detectTrigger(editor);
          return;
        }
        onChangeRef.current?.(
          editor.getJSON(),
          jsonToPlainMarker(editor.getJSON()),
        );
        detectTrigger(editor);
      },
      onSelectionUpdate: ({ editor }) => {
        if (readOnlyRef.current) return;
        detectFieldSelection(editor);
        detectTrigger(editor);
      },
    });

    /* 필드 모드에서 inline contractField 가 NodeSelection 되면 설정 팝오버를 띄운다. */
    const detectFieldSelection = (ed: Editor) => {
      if (!fieldModeRef.current) {
        setFieldSel(null);
        return;
      }
      const sel = ed.state.selection as any;
      const node = sel?.node;
      if (
        sel instanceof NodeSelection &&
        node?.type?.name === "contractField"
      ) {
        try {
          const coords = ed.view.coordsAtPos(sel.from);
          setFieldSel({
            from: sel.from,
            attrs: { ...node.attrs },
            rect: {
              left: coords.left,
              top: coords.top,
              bottom: coords.bottom,
            },
          });
        } catch {
          setFieldSel(null);
        }
      } else {
        setFieldSel(null);
      }
    };

    /* 선택된 필드의 attrs 갱신 (필수/선택·도장 등). NodeSelection 을 유지해 팝오버가 안 닫히게 함. */
    const updateSelectedField = (patch: Record<string, any>) => {
      if (!editor || !fieldSel) return;
      const pos = fieldSel.from;
      editor
        .chain()
        .command(({ tr, state, dispatch }) => {
          const node = state.doc.nodeAt(pos);
          if (!node || node.type.name !== "contractField") return false;
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch });
            tr.setSelection(NodeSelection.create(tr.doc, pos));
          }
          return true;
        })
        .run();
      setFieldSel((prev) =>
        prev ? { ...prev, attrs: { ...prev.attrs, ...patch } } : prev,
      );
    };
    /* 선택된 필드 삭제 */
    const deleteSelectedField = () => {
      if (!editor) return;
      editor.chain().focus().deleteSelection().run();
      setFieldSel(null);
    };

    useEffect(() => {
      editor?.setEditable(!readOnly);
    }, [editor, readOnly]);

    // 서명 모드: 마운트 직후 초기 진행률을 한 번 밀어준다.
    // (필드 상호작용 전엔 콜백이 안 불려 부모가 진행률을 null 로 오인 → 게이트가 열려버리는 문제 방지)
    useEffect(() => {
      if (signMode && editor) pushSignProgress();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, signMode]);

    useEffect(() => {
      if (!editor || readOnly || didSyncInitialDocRef.current) return;
      didSyncInitialDocRef.current = true;
      onChangeRef.current?.(
        editor.getJSON(),
        jsonToPlainMarker(editor.getJSON()),
      );
    }, [editor, readOnly]);

    useImperativeHandle(
      ref,
      () => ({
        getJSON: () =>
          editor?.getJSON() ?? initialDocRef.current ?? emptyDocWithPage(),
        getText: () => {
          const json =
            editor?.getJSON() ?? initialDocRef.current ?? emptyDocWithPage();
          return jsonToPlainMarker(json);
        },
        getZoom: () => zoomPct / 100,
        insertField: (type: string) => {
          if (!editor) return;

          // 커서(배치 위치)가 없거나 화면 밖이면 삽입하지 않고 안내만 띄운다.
          //  → 사용자가 "필드를 넣을 위치를 먼저 본문에서 클릭"하도록 유도.
          const sel = editor.state.selection;
          let caretOk = false;
          try {
            const head = sel.head;
            const placed = editor.isFocused && sel.empty && head > 0;
            if (placed) {
              const $h = editor.state.doc.resolve(head);
              if ($h.parent.inlineContent) {
                const view = editor.view;
                const rect = (view.dom as HTMLElement).getBoundingClientRect();
                const visTop = Math.max(rect.top, 0);
                const visBottom = Math.min(rect.bottom, window.innerHeight);
                const caret = view.coordsAtPos(head);
                caretOk =
                  caret.top >= visTop - 4 && caret.bottom <= visBottom + 4;
              }
            }
          } catch {
            caretOk = false;
          }

          if (!caretOk) {
            showCaretNotice();
            return;
          }

          const node = {
            type: "contractField",
            attrs: {
              fieldType: type,
              fieldKey: fieldDefaultKey(type),
              label: fieldDefaultLabel(type),
              alignment: "left",
              required: true,
            },
          };
          editor
            .chain()
            .focus()
            .insertContentAt(editor.state.selection.head, node)
            .run();
        },
        getSignValues: () => {
          // NodeView 가 실제로 write 하는 스토어(editor.storage.sign)를 우선 읽는다.
          // HMR/재마운트로 signStoreRef 와 editor.storage 가 어긋나면 서명값이 유실되던 버그 방지.
          const s = (editor?.storage as any)?.sign ?? signStoreRef.current;
          return {
            fields: s ? { ...s.values } : {},
            checkboxes: s ? { ...s.checkboxes } : {},
            signature: s?.signature ?? null,
          };
        },
        validateSign: () => {
          const s = (editor?.storage as any)?.sign ?? signStoreRef.current;
          if (!s) return null;
          for (const f of signFieldsRef.current) {
            if (!f.required) continue;
            if (f.type === "signature") {
              if (!s.signature) return "보호자 서명을 해주세요.";
            } else if (f.type === "checkbox") {
              if (!s.checkboxes[f.key]) return "필수 동의 항목을 확인해주세요.";
            } else if (!s.values[f.key]) {
              return "필수 입력 항목을 모두 채워주세요.";
            }
          }
          return null;
        },
        getSignProgress: () => {
          const s = (editor?.storage as any)?.sign ?? signStoreRef.current;
          const fields = signFieldsRef.current;
          const isFilled = (f: { type: string; key: string }) => {
            if (!s) return false;
            if (f.type === "signature") return !!s.signature;
            if (f.type === "checkbox") return !!s.checkboxes[f.key];
            return !!s.values[f.key];
          };
          let done = 0;
          let total = 0;
          let optionalDone = 0;
          let optionalTotal = 0;
          fields.forEach((f) => {
            if (f.required) {
              total++;
              if (isFilled(f)) done++;
            } else {
              optionalTotal++;
              if (isFilled(f)) optionalDone++;
            }
          });
          return { done, total, optionalDone, optionalTotal };
        },
        scrollToNextIncomplete: () => {
          const s = (editor?.storage as any)?.sign ?? signStoreRef.current;
          if (!s) return;
          const next = signFieldsRef.current.find((f) => {
            if (f.type === "signature") return !s.signature;
            if (f.type === "checkbox") return !s.checkboxes[f.key];
            return !s.values[f.key];
          });
          if (!next) return;
          const el = document.querySelector<HTMLElement>(
            `[data-field-key="${next.key}"]`,
          );
          el?.scrollIntoView({ block: "center", behavior: "smooth" });
          // 필드로 스크롤 → 보호자가 해당 필드를 탭하면 필드 자체 팝업(서명패드 등)이 열린다.
          (el?.querySelector("button, input") as HTMLElement | null)?.focus?.();
        },
      }),
      [editor, zoomPct],
    );

    /* ── 자동 페이지네이션 + 표 폭 자동 정규화 플러그인 등록 ──────────────── */
    useEffect(() => {
      if (!editor) return;
      const pgPlugin = buildPaginationPlugin();
      const tblPlugin = buildTableNormalizePlugin();
      editor.registerPlugin(pgPlugin);
      editor.registerPlugin(tblPlugin);
      return () => {
        try {
          editor.unregisterPlugin(pgPlugin.spec.key as any);
        } catch {
          /* ignore */
        }
        try {
          editor.unregisterPlugin(tblPlugin.spec.key as any);
        } catch {
          /* ignore */
        }
      };
    }, [editor]);

    /* ── DOM 단계 최종 안전망 — 모든 표의 <col> width 합이 644 이내 되도록 강제.
     TipTap 의 table extension 이 우리 ProseMirror 정규화를 우회해도, 또는 캐시된 docJson 이
     아직 644 밖 값을 가지고 있어도 화면 단계에서 무조건 644 안에 가두기 위한 마지막 layer.
     MutationObserver + rAF 로 DOM 변경 감지 후 즉시 보정. */
    useEffect(() => {
      if (!editor) return;
      const PAGE_INNER = 644;
      let scheduled = 0;

      const fitTablesInDOM = () => {
        scheduled = 0;
        const root = editor.view.dom as HTMLElement;
        const tables = root.querySelectorAll<HTMLTableElement>("table");
        tables.forEach((tbl) => {
          const cols = Array.from(
            tbl.querySelectorAll<HTMLTableColElement>("colgroup > col"),
          );
          if (!cols.length) return;
          const widths = cols.map((c) => parseFloat(c.style.width || "0"));
          const sum = widths.reduce((a, b) => a + b, 0);
          // overflow 만 (sum > 644+2) 강제 축소. underflow 는 rowspan 누락이므로 두기.
          if (sum > PAGE_INNER + 2) {
            const scale = PAGE_INNER / sum;
            cols.forEach((c, i) => {
              const newW = Math.max(24, Math.floor(widths[i] * scale));
              c.style.width = `${newW}px`;
            });
            tbl.style.width = `${PAGE_INNER}px`;
            tbl.style.maxWidth = `${PAGE_INNER}px`;
          }
        });
      };

      const schedule = () => {
        if (scheduled) return;
        scheduled = requestAnimationFrame(fitTablesInDOM);
      };

      // 초기 1회 + DOM 변경 시마다
      schedule();
      const obs = new MutationObserver(schedule);
      obs.observe(editor.view.dom, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "colwidth"],
      });
      return () => {
        obs.disconnect();
        if (scheduled) cancelAnimationFrame(scheduled);
      };
    }, [editor]);

    /* ── 트리거 감지 (/ 또는 [) ─────────────────────── */
    const detectTrigger = (ed: Editor) => {
      if (!ed.state.selection.empty) {
        setTrigger(null);
        return;
      }
      const { from } = ed.state.selection;
      if (from <= 0) {
        setTrigger(null);
        return;
      }
      const $from = ed.state.selection.$from;
      const lineStart = $from.start();
      const before = ed.state.doc.textBetween(
        Math.max(lineStart, from - 30),
        from,
        "\n",
        "\n",
      );
      const slash = /(?:^|\s)(\/)([^\s/[\]]*)$/.exec(before);
      const bracket = /(?:^|\s)(\[)([^[\]\s]*)$/.exec(before);
      const matched = slash || bracket;
      if (!matched) {
        setTrigger(null);
        return;
      }
      const triggerChar = matched[1];
      const query = matched[2] ?? "";
      const triggerLen = 1 + query.length;
      const triggerFrom = from - triggerLen;
      try {
        const coords = ed.view.coordsAtPos(triggerFrom);
        setTrigger({
          type: triggerChar === "/" ? "slash" : "bracket",
          from: triggerFrom,
          query,
          rect: { left: coords.left, top: coords.top, bottom: coords.bottom },
        });
        setHighlightIdx(0);
      } catch {
        setTrigger(null);
      }
    };

    const triggerItems = trigger
      ? buildTriggerItems(trigger.type, trigger.query)
      : [];

    const applyTriggerItem = (item: TriggerItem) => {
      if (!editor || !trigger) return;
      const len = 1 + trigger.query.length;
      const from = trigger.from;
      const to = from + len;

      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent({
          type: "contractVariable",
          attrs: { varKey: item.key, label: item.label },
        })
        .run();
      setTrigger(null);
    };

    /* ── 트리거 메뉴 키보드 네비게이션 ─────────────────── */
    useEffect(() => {
      if (!trigger) return;
      const onKey = (e: KeyboardEvent) => {
        if (!triggerItems.length) return;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightIdx((i) => Math.min(i + 1, triggerItems.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          applyTriggerItem(triggerItems[highlightIdx]);
        } else if (e.key === "Escape") {
          setTrigger(null);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [trigger, triggerItems, highlightIdx]);

    if (!editor) return null;

    return (
      <div className="border border-[#E9E9E9] rounded-md bg-[#E5E7EB] relative flex flex-col flex-1 min-h-0 min-w-0">
        {!readOnly && (
          <ContractEditorToolbar
            editor={editor}
            zoomPct={zoomPct}
            fieldMode={fieldMode}
            onZoomIn={() => panZoomRef.current?.zoomIn()}
            onZoomOut={() => panZoomRef.current?.zoomOut()}
            onZoomReset={() => panZoomRef.current?.reset100()}
            onZoomFit={() => panZoomRef.current?.fit()}
          />
        )}

        {/* 트리거 floating 메뉴 */}
        {trigger && triggerItems.length > 0 && (
          <div
            className="fixed z-50 bg-white border border-[#E9E9E9] rounded-lg shadow-lg w-[260px] max-h-[280px] overflow-y-auto"
            style={{
              left: Math.min(trigger.rect.left, window.innerWidth - 280),
              top: trigger.rect.bottom + 4,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="px-2 py-1 text-[10px] font-bold text-[#999] border-b">
              {trigger.type === "slash" ? "빠른 삽입" : "변수 검색"}
              {trigger.query && (
                <span className="text-[#FF9934]"> · "{trigger.query}"</span>
              )}
            </div>
            {triggerItems.map((item, i) => (
              <button
                key={`${item.kind}-${item.key}-${i}`}
                type="button"
                onMouseEnter={() => setHighlightIdx(i)}
                onClick={() => applyTriggerItem(item)}
                className={`w-full text-left px-2 py-1.5 text-[13px] flex items-center gap-2 ${
                  i === highlightIdx ? "bg-[#FFF7E6]" : "hover:bg-[#FAFAFA]"
                }`}
              >
                <span className="w-5 text-center text-[12px] text-[#999]">
                  {item.group === "auto" ? "🟢" : "🟡"}
                </span>
                <span className="flex-1">{item.label}</span>
                {item.kind === "variable" && (
                  <span className="text-[10px] text-[#BBB]">[{item.key}]</span>
                )}
              </button>
            ))}
            <div className="px-2 py-1 text-[10px] text-[#BBB] border-t bg-[#FAFAFA]">
              ↑↓ 이동 · Enter 선택 · Esc 닫기
            </div>
          </div>
        )}

        {/* 필드 추가 시 커서 미배치 안내 토스트 */}
        {caretNotice && (
          <div className="fixed left-1/2 top-24 z-[60] -translate-x-1/2 rounded-lg bg-[#333] px-3 py-2 text-[12px] text-white shadow-lg">
            먼저 본문에서 <b>넣을 위치를 클릭</b>한 뒤 다시 선택해주세요.
          </div>
        )}

        {/* 필드 설정 팝오버 — 필드 모드에서 inline 필드 클릭 시 노출 */}
        {fieldSel &&
          !fieldDragging &&
          (() => {
            const a = fieldSel.attrs;
            const isRequired = a.required !== false;
            const isSeal = a.fieldType === "seal";
            let curSeal = "";
            if (a.fieldOptions) {
              try {
                const fo =
                  typeof a.fieldOptions === "string"
                    ? JSON.parse(a.fieldOptions)
                    : a.fieldOptions;
                if (fo?.sealSeq != null) curSeal = String(fo.sealSeq);
              } catch {
                /* ignore */
              }
            }
            return (
              <div
                className="fixed z-50 bg-white border border-[#E9E9E9] rounded-lg shadow-lg p-1.5"
                style={{
                  left: Math.min(fieldSel.rect.left, window.innerWidth - 170),
                  top: fieldSel.rect.bottom + 4,
                  width: 150,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="flex items-center gap-1 mb-1 text-[10px] font-bold text-[#666]">
                  <span>{fieldIcon(a.fieldType)}</span>
                  <span style={{ color: fieldColor(a.fieldType) }}>
                    {friendlyFieldLabel(a.fieldType, a.label, a.fieldKey)}
                  </span>
                </div>
                {/* 필수 / 선택 토글 (컴팩트) */}
                <div className="flex gap-1 mb-1.5">
                  <button
                    type="button"
                    onClick={() => updateSelectedField({ required: true })}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                      isRequired
                        ? "bg-[#FF4D4F] text-white"
                        : "bg-[#F5F5F5] text-[#999]"
                    }`}
                  >
                    필수
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSelectedField({ required: false })}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                      !isRequired
                        ? "bg-[#1677FF] text-white"
                        : "bg-[#F5F5F5] text-[#999]"
                    }`}
                  >
                    선택
                  </button>
                </div>
                {/* 크기 조절 안내 — 실제 조절은 필드 우하단 모서리 핸들 드래그 */}
                {a.fieldType !== "checkbox" && (
                  <div className="mb-1 text-[9px] text-[#999] leading-tight">
                    우상단=크기 · 본체=이동
                  </div>
                )}
                {/* 도장 선택 (seal 필드 전용) */}
                {isSeal && (
                  <select
                    value={curSeal}
                    onChange={(e) =>
                      updateSelectedField({
                        fieldOptions: e.target.value
                          ? { sealSeq: Number(e.target.value) }
                          : null,
                      })
                    }
                    className="w-full mb-1 px-1 py-0.5 text-[10px] border border-[#E9E9E9] rounded"
                  >
                    <option value="">기본 도장</option>
                    {activeSeals.map((s) => (
                      <option key={s.sealSeq} value={String(s.sealSeq)}>
                        {s.sealName ?? `도장 ${s.sealSeq}`}
                        {s.defaultYn === "Y" ? " (기본)" : ""}
                      </option>
                    ))}
                  </select>
                )}
                {/* 삭제 */}
                <button
                  type="button"
                  onClick={deleteSelectedField}
                  className="w-full px-2 py-0.5 text-[10px] font-semibold rounded bg-white border border-[#FF4D4F] text-[#FF4D4F] hover:bg-[#FFF1F0]"
                >
                  필드 삭제
                </button>
              </div>
            );
          })()}

        {/* 줌·팬 캔버스 */}
        <PanZoomCanvas
          ref={panZoomRef}
          contentWidth={A4.WIDTH_PX}
          contentHeight={A4.HEIGHT_PX}
          fillParent
          hideToolbar
          onZoomChange={(z) => setZoomPct(Math.round(z * 100))}
        >
          <div
            className="contract-page-stack"
            style={{ width: A4.WIDTH_PX, position: "relative" }}
          >
            <EditorContent editor={editor} />
            {/* 오버레이 — 필드 배치 박스 등이 editor 위에 얹힘 (같은 좌표계·같은 zoom).
              inset:0 로 스택 전체(모든 페이지 총 높이)를 커버해 페이지 2·3 필드도 표시됨. */}
            {overlay != null && (
              <div
                className="contract-overlay-layer"
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              >
                {overlay}
              </div>
            )}
          </div>
        </PanZoomCanvas>

        {/* 하단 상태 바 — 페이지 번호 / 글자 수 / 단어 수 / 줌 */}
        <ContractEditorStatusBar editor={editor} zoomPct={zoomPct} />

        <style>{`
        /* contractPage paragraph 기본 타이포그래피 — HWP 와 페이지 수 일치 위해 컴팩트하게.
           font-size: 10pt · line-height: 1.5 (HWP 와 비슷한 줄밀도) · letter-spacing: -0.04em (자간 -4%) */
        .contract-page-canvas p {
          margin-top: 0;
          margin-bottom: 0;
          line-height: 1.5;
          letter-spacing: -0.04em;
          font-family: "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", "Noto Sans CJK KR", sans-serif;
          font-size: 10pt;
          color: #111;
        }
        /* HWP 는 단락 사이 자동 spacing 없음 — 0 으로 (개행은 빈 단락으로 처리됨) */
        .contract-page-canvas p + p { margin-top: 0; }

        /* 직접 작성·임포트 paragraph — contractPage 의 직접 자식만 (table 내부 paragraph 는 별도 reset) */
        .contract-page-canvas > p,
        .contract-page-canvas > h1,
        .contract-page-canvas > h2,
        .contract-page-canvas > h3 {
          /* width 정확히 644 강제 — text-align center/right 가 페이지 정중앙·우측 끝에 자리잡도록 */
          margin-left: 75px;
          margin-right: 75px;
          width: 644px;
          max-width: 644px;
          box-sizing: border-box;

          /* 띄어쓰기·자간 보존 + 가로 overflow 방지 */
          overflow-wrap: anywhere;
          word-break: keep-all;
          white-space: pre-wrap;
          tab-size: 4;
          -moz-tab-size: 4;

          /* text-align 은 명시하지 않음 — 인라인 style="text-align:.." (TipTap TextAlign) 이 자연 적용 */
        }
        /* 인라인 style 변형(공백 유무, 세미콜론 등) 모두 catch — !important 로 외부 영향 차단 */
        .contract-page-canvas > p[style*="text-align: center"],
        .contract-page-canvas > p[style*="text-align:center"]   { text-align: center !important; }
        .contract-page-canvas > p[style*="text-align: right"],
        .contract-page-canvas > p[style*="text-align:right"]    { text-align: right !important; }
        .contract-page-canvas > p[style*="text-align: justify"],
        .contract-page-canvas > p[style*="text-align:justify"]  { text-align: justify !important; }
        /* attrs.textAlign 값을 데이터 속성으로도 동시에 검사 (TipTap 일부 빌드에서 data 속성 추가) */
        .contract-page-canvas > p[data-text-align="center"]  { text-align: center !important; }
        .contract-page-canvas > p[data-text-align="right"]   { text-align: right !important; }
        .contract-page-canvas > p[data-text-align="justify"] { text-align: justify !important; }

        /* 페이지 상단 75px 여백 — contractPage 자체에 padding-top 을 주면 absolute 자식 좌표
           계산이 깨지므로, ::before 가상 요소로 75px 공간 확보. 이 방식은 모든 첫 자식에 일관 적용. */
        .contract-page-canvas::before {
          content: "";
          display: block;
          height: 75px;
          width: 100%;
        }
        /* 기존 :first-child padding 룰은 유지 (호환성) — 가상 요소와 중복되지 않게 :first-child 만 */
        .contract-page-canvas > p:first-child,
        .contract-page-canvas > h1:first-child,
        .contract-page-canvas > h2:first-child,
        .contract-page-canvas > h3:first-child,
        .contract-page-canvas > div.tableWrapper:first-child,
        .contract-page-canvas > table:first-child {
          padding-top: 0;
        }

        /* 표 — 임포트된 계약서의 테두리 시각화.
           단락(p)과 동일한 좌우 마진 75px 안에서 풀폭 644px — 본문 라인과 좌우 정렬 통일.
           !important 로 TipTap table extension 의 자체 width 계산 (col widths 합) 덮어씀. */
        .contract-page-canvas table.contract-table,
        .contract-page-canvas table {
          border-collapse: collapse;
          margin-top: 6pt;
          margin-bottom: 6pt;
          margin-left: 75px !important;
          margin-right: 75px !important;
          table-layout: fixed;
          width: 644px !important;
          min-width: 644px !important;
          max-width: 644px !important;
          box-sizing: border-box;
          font-family: "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", "Noto Sans CJK KR", sans-serif;
          font-size: 11pt;
        }
        /* TipTap table 의 <col> 요소 — contractExtract 에서 합이 644 가 되도록 정규화함.
           추가 안전망으로 colgroup 도 644 로 캡 (어떤 경우에도 표가 페이지 폭을 초과하지 않음). */
        .contract-page-canvas table colgroup {
          width: 644px !important;
          max-width: 644px !important;
        }
        .contract-page-canvas table col {
          max-width: 644px;
        }
        /* TipTap 이 표를 <div class="tableWrapper"> 로 감쌈 — 이 래퍼도 페이지 본문 폭(644)에 가둠.
           overflow-x: hidden 으로 어떤 케이스에도 페이지 옆으로 새지 않게. */
        .contract-page-canvas .tableWrapper {
          margin-left: 75px !important;
          margin-right: 75px !important;
          max-width: 644px !important;
          width: 644px !important;
          overflow-x: hidden;
          box-sizing: border-box;
        }
        .contract-page-canvas .tableWrapper > table {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        .contract-page-canvas table th,
        .contract-page-canvas table td {
          border: 0.6pt solid #969696;
          padding: 2pt 4pt;                /* 더 컴팩트 (3pt 5pt → 2pt 4pt) */
          vertical-align: middle;
          line-height: 1.3;
          /* 셀 안의 긴 unbreakable 텍스트가 셀을 옆으로 늘리지 않도록 강제 줄바꿈. */
          word-break: break-all;
          overflow-wrap: anywhere;
          overflow: hidden;
        }
        /* 표 안 paragraph 도 본문과 동일 typo (더 컴팩트) */
        .contract-page-canvas table p {
          line-height: 1.3;
          letter-spacing: -0.04em;
        }
        /* 페이지 보호 — 어떤 자식도 contractPage 폭(794) 을 넘지 않도록 안전장치. */
        .contract-page-canvas > * {
          max-width: 794px !important;
          box-sizing: border-box;
        }
        .contract-page-canvas table th {
          background: #F5F5F5;
          font-weight: 700;
        }
        /* 표 1셀 행 = 한컴오피스 섹션 헤더 후보 (예: "결제 정보", "비상연락 보호자 정보", "반려견 정보").
           본문 텍스트(좌정렬)와 헤더(가운데정렬)를 단락의 textAlign 으로 구분 — 가운데 정렬된 paragraph 를
           포함한 1셀 셀만 회색 음영 처리. 개인정보 동의 본문(좌정렬) 은 자연히 제외됨. */
        .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[style*="text-align: center"]),
        .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[style*="text-align:center"]),
        .contract-page-canvas table tr:has(> td:only-child) > td:has(> p[data-text-align="center"]) {
          background: #F5F5F5;
          font-weight: 700;
        }
        /* 1셀 행 (개인정보 동의 본문 등) 의 좌우 padding 제거 — 셀 내 컨텐츠가 644px 전체를 사용해
           본문 단락과 동일한 폭으로 보이도록. 데이터 표 (다중 셀 행) 의 padding 은 유지. */
        .contract-page-canvas table tr > td:only-child {
          padding-left: 0;
          padding-right: 0;
        }
        /* 표 안의 paragraph 는 페이지 좌우 마진(75px) 영향 받지 않도록 reset */
        .contract-page-canvas table p,
        .contract-page-canvas table th p,
        .contract-page-canvas table td p {
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-top: 0 !important;
          max-width: none !important;
        }
        /* 표 안 단락 사이 spacing 도 0 — HWP 셀 내부 컴팩트한 느낌 */
        .contract-page-canvas table td p + p,
        .contract-page-canvas table th p + p {
          margin-top: 0 !important;
        }

        /* 선택 영역 가시성 — 흰 페이지 위에서 잘 보이도록 */
        .contract-page-canvas ::selection {
          background-color: rgba(255, 153, 52, 0.25);
          color: inherit;
        }
        /* 커서 깜빡임 강조 — ProseMirror caret */
        .contract-page-canvas .ProseMirror-focused {
          caret-color: #FF9934;
        }
      `}</style>
      </div>
    );
  },
);

ContractRichEditor.displayName = "ContractRichEditor";

export default ContractRichEditor;
